// Simple direct browser upload to Cloudinary using unsigned presets

const UPLOAD_PRESET = 'image_generator';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

export interface CloudinaryAsset {
  publicId: string;
  url: string;
  secureUrl: string;
  width: number;
  height: number;
  format: string;
  bytes: number;
  createdAt: string;
}

export interface CloudinaryUploadResponse {
  success: boolean;
  asset?: CloudinaryAsset;
  error?: string;
}

class CloudinaryDirectClient {
  private getCloudName(): string {
    const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
    if (!cloudName) {
      throw new Error('VITE_CLOUDINARY_CLOUD_NAME environment variable not configured');
    }
    return cloudName;
  }

  private validateFile(file: File): void {
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = Math.round(file.size / (1024 * 1024) * 100) / 100;
      throw new Error(`File too large. Maximum size is 10MB, received ${sizeMB}MB`);
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`Invalid file type. Allowed: ${ALLOWED_TYPES.join(', ')}. Received: ${file.type}`);
    }
  }

  async uploadImage(file: File): Promise<CloudinaryUploadResponse> {
    try {
      console.log(`[Cloudinary] Starting upload: ${file.name}`);
      
      // Validate file
      this.validateFile(file);
      
      // Prepare upload
      const cloudName = this.getCloudName();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', UPLOAD_PRESET);

      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
      console.log(`[Cloudinary] Uploading with preset: ${UPLOAD_PRESET}`);
      
      // Upload to Cloudinary
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Cloudinary] Upload failed:`, errorText);
        throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`[Cloudinary] Upload successful: ${result.secure_url}`);
      
      return {
        success: true,
        asset: {
          publicId: result.public_id,
          url: result.url,
          secureUrl: result.secure_url,
          width: result.width,
          height: result.height,
          format: result.format,
          bytes: result.bytes,
          createdAt: result.created_at,
        }
      };
    } catch (error) {
      console.error('[Cloudinary] Upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  generateUrl(publicId: string): string {
    const cloudName = this.getCloudName();
    return `https://res.cloudinary.com/${cloudName}/image/upload/${publicId}`;
  }

  generateThumbnail(publicId: string, size: number = 150): string {
    const cloudName = this.getCloudName();
    return `https://res.cloudinary.com/${cloudName}/image/upload/w_${size},h_${size},c_fill,f_auto,q_auto/${publicId}`;
  }
}

export const cloudinaryClient = new CloudinaryDirectClient(); 